import { AdminLayout } from "./AdminLayout";
import { Plus, Search, MoreVertical, Mail, Phone, User } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', job_title: '', company_id: '', notes: ''
  });

  const fetchData = async () => {
    try {
      const [contactsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/crm/contacts`, { withCredentials: true }),
        axios.get(`${API}/crm/companies`, { withCredentials: true })
      ]);
      setContacts(contactsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await axios.put(`${API}/crm/contacts/${editingContact.contact_id}`, formData, { withCredentials: true });
        toast.success("Contact updated");
      } else {
        await axios.post(`${API}/crm/contacts`, formData, { withCredentials: true });
        toast.success("Contact created");
      }
      setIsDialogOpen(false);
      setEditingContact(null);
      setFormData({ name: '', email: '', phone: '', job_title: '', company_id: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error("Failed to save contact");
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name, email: contact.email, phone: contact.phone || '',
      job_title: contact.job_title || '', company_id: contact.company_id || '', notes: contact.notes || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axios.delete(`${API}/crm/contacts/${contactId}`, { withCredentials: true });
      toast.success("Contact deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    return company?.name || '';
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout title="Contacts">
      <div className="space-y-6" data-testid="admin-contacts">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Contacts</h2>
            <p className="text-sm text-[#8A8A8A]">{contacts.length} total</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2"
                onClick={() => { setEditingContact(null); setFormData({ name: '', email: '', phone: '', job_title: '', company_id: '', notes: '' }); }}
                data-testid="add-contact-btn">
                <Plus className="w-4 h-4" /> Add Contact
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
              <DialogHeader><DialogTitle>{editingContact ? 'Edit Contact' : 'Add New Contact'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Full Name *" required value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-field" />
                  <input type="email" placeholder="Email *" required value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})} className="input-field" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input type="tel" placeholder="Phone" value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})} className="input-field" />
                  <input type="text" placeholder="Job Title" value={formData.job_title}
                    onChange={(e) => setFormData({...formData, job_title: e.target.value})} className="input-field" />
                </div>
                <select value={formData.company_id} onChange={(e) => setFormData({...formData, company_id: e.target.value})} className="input-field">
                  <option value="">Select Company</option>
                  {companies.map(company => (
                    <option key={company.company_id} value={company.company_id}>{company.name}</option>
                  ))}
                </select>
                <textarea placeholder="Notes" rows={3} value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})} className="input-field resize-none" />
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm">{editingContact ? 'Update' : 'Create'} Contact</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
          <input type="text" placeholder="Search contacts..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} className="input-field pl-10 w-full sm:w-96" />
        </div>

        <div className="border border-[#2E2E2E] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1C1C1C] border-b border-[#2E2E2E]">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Contact</th>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Email</th>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Phone</th>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Company</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-b border-[#2E2E2E]">
                      <td colSpan={5} className="p-4"><div className="h-4 bg-[#2E2E2E] rounded animate-pulse" /></td>
                    </tr>
                  ))
                ) : filteredContacts.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-[#8A8A8A]">No contacts found</td></tr>
                ) : (
                  filteredContacts.map((contact) => (
                    <tr key={contact.contact_id} className="border-b border-[#2E2E2E] hover:bg-[#1C1C1C]">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#2E2E2E] rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-[#8A8A8A]" />
                          </div>
                          <div>
                            <div className="font-medium">{contact.name}</div>
                            {contact.job_title && <div className="text-sm text-[#8A8A8A]">{contact.job_title}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm flex items-center gap-1"><Mail className="w-3 h-3 text-[#8A8A8A]" />{contact.email}</div>
                      </td>
                      <td className="p-4">
                        {contact.phone ? (
                          <div className="text-sm flex items-center gap-1"><Phone className="w-3 h-3 text-[#8A8A8A]" />{contact.phone}</div>
                        ) : <span className="text-[#8A8A8A]">—</span>}
                      </td>
                      <td className="p-4 text-sm">{getCompanyName(contact.company_id) || <span className="text-[#8A8A8A]">—</span>}</td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 hover:bg-[#2E2E2E]"><MoreVertical className="w-4 h-4" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                            <DropdownMenuItem onClick={() => handleEdit(contact)} className="cursor-pointer hover:bg-[#2E2E2E]">Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(contact.contact_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Contacts;
